import { useState, useEffect } from 'react';

interface UseReleaseNoteModalProps {
  shouldShow: boolean;
  delay?: number;
}

interface UseReleaseNoteModalReturn {
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

/**
 * Custom hook to manage release note modal state with auto-open functionality
 * @param shouldShow - Whether the modal should be shown (based on user state, permissions, etc.)
 * @param delay - Delay in milliseconds before auto-opening (default: 1000ms)
 * @returns Modal state and control functions
 */
export const useReleaseNoteModal = ({
  shouldShow,
  delay = 1000,
}: UseReleaseNoteModalProps): UseReleaseNoteModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  // Auto-open logic
  useEffect(() => {
    if (shouldShow && !hasShown) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasShown(true);
      }, delay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shouldShow, hasShown, delay]);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    openModal,
    closeModal,
  };
};

export default useReleaseNoteModal;
